import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { LoadingService } from '../../../services/loading.service';
import { AuthService } from '../../../services/auth.service';
import { ChatService } from '../../../services/chat.service';
import { AgencySidebarComponent } from '../sidebar/agency-sidebar.component';
import { AgencyHeaderComponent } from '../agency-header/agency-header.component';
import { FooterComponent } from '../components/footer/footer.component';
import { PrivacyBannerComponent } from '../components/privacy-banner/privacy-banner.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ChatPopupComponent } from './components/chat-popup/chat-popup.component';
import { AgencySidebarMobileComponent } from '../sidebar/agency-sidebar-mobile.component';

@Component({
  selector: 'app-agency-layout-xontainer',
  templateUrl: './agency-layout-xontainer.component.html',
  styleUrls: ['./agency-layout-xontainer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AgencySidebarComponent,
    AgencySidebarMobileComponent,
    AgencyHeaderComponent,
    FooterComponent,
    PrivacyBannerComponent,
    AngularSvgIconModule,
    ChatPopupComponent
  ],
})
export class AgencyLayoutXontainerComponent implements OnInit, OnDestroy {
  menuItems:any = [
    {
      desc: 'Gestion des établissements',
      path: '',
      btnText: 'ACCÉDER AUX ÉTABLISSEMENTS'
    },
    {
      desc: 'Gestion des professionnels de santé',
      path: '',
      btnText: 'ACCÉDER AUX PROFESSIONNELS'
    },
    // Lot C (client clinique) : « Gestion de vos agences » masquée — code conservé
    // {
    //   desc: 'Gestion de vos agences',
    //   path: 'agency/agencies',
    //   btnText: 'ACCÉDER AUX AGENCES'
    // },
    {
      desc: 'Gestions de vos accès',
      path: 'agency/users',
      btnText: 'ACCÉDER AUX UTILISATEURS'
    },
    {
      desc: 'Gestions des missions',
      path: 'agency/missions',
      btnText: 'ACCÉDER AUX MISSIONS'
    },
  ]

  title = 'collary-front';
  loading = false;
  subscription: Subscription;
  isAuthenticated = false;
  isChatOpen = false;
  isChatActive = true; // Control whether chat feature is visible
  totalUnreadCount = 0;
  private unreadCountSubscription: Subscription | null = null;
  private unreadRefreshInterval: any;
  private chatOpenSubscription: Subscription | null = null;
  private chatActiveSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private lastAccessRefresh = 0;

  constructor(
    public loadingService: LoadingService,
    private authService: AuthService,
    private chatService: ChatService,
    private router: Router
  ) {}

  ngOnInit() {
    this.subscription = this.loadingService.getLoading().subscribe((value) => {
      this.loading = value;
    });

    // Role / permissions may have been changed by an admin: refresh them now and on each
    // navigation (throttled) so menus and actions follow without a new login. A disabled account
    // gets a 401 here and is sent back to the login page by the interceptor.
    this.refreshAccess();
    this.routerSubscription = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.refreshAccess());

    // Subscribe to chat active status changes
    this.chatActiveSubscription = this.chatService.chatActive$.subscribe((isActive: boolean) => {
      this.isChatActive = isActive;
    });

    // Load initial chat active status
    this.isChatActive = this.chatService.isChatActive;

    // Subscribe to user authentication state
    this.authService.getUser().subscribe(user => {
      this.isAuthenticated = !!user;
      if (user && user.id) {
        // Unread chat messages are counted for the AGENCY (shared inbox), not the collaborator.
        const agencyId = this.authService.agencyId ?? user.id;
        // Load initial unread count
        this.loadTotalUnreadCount(agencyId);

        // Subscribe to unread count updates from WebSocket
        //this.unreadCountSubscription = this.chatService.unreadCount$.subscribe(() => {
          // Only reload if chat is NOT open
          //if (!this.isChatOpen) {
            //this.loadTotalUnreadCount(user.id);
          //}
       // });

        // Subscribe to chat open/close events (the user stream re-emits when the role is refreshed:
        // never stack a second subscription, it would subtract the count twice).
        this.chatOpenSubscription?.unsubscribe();
        this.chatOpenSubscription = this.chatService.chatOpen$.subscribe((count: number) => {
          this.isChatOpen = count > 0;
          if (this.isChatOpen) {
            // When chat opens, immediately reset counter to 0
            this.totalUnreadCount -= count;
          } 
          //else {
            // When chat closes, reload the count
            // this.loadTotalUnreadCount(user.id);
          //}
        });

        // Refresh unread count every 10 seconds
        if (this.unreadRefreshInterval) {
          clearInterval(this.unreadRefreshInterval);
        }
        this.unreadRefreshInterval = setInterval(() => {
          //if (!this.isChatOpen) {
            this.loadTotalUnreadCount(agencyId);
          // }
        }, 5000);
      }
    });
  }

  private refreshAccess(): void {
    const now = Date.now();
    if (now - this.lastAccessRefresh < 30000) {
      return;
    }
    this.lastAccessRefresh = now;
    this.authService.refreshCurrentUser().subscribe({ error: () => {} });
  }

  loadTotalUnreadCount(userId: number): void {
    if (this.isChatActive) { 
      this.chatService.getTotalUnreadCount(userId, 'AGENCY').subscribe({
        next: (count) => {
          this.totalUnreadCount = count;
        },
        error: (error) => {
          console.error('Error loading unread count:', error);
        }
      });
    }  
  }

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.routerSubscription?.unsubscribe();
    if (this.unreadCountSubscription) {
      this.unreadCountSubscription.unsubscribe();
    }
    if (this.chatOpenSubscription) {
      this.chatOpenSubscription.unsubscribe();
    }
    if (this.chatActiveSubscription) {
      this.chatActiveSubscription.unsubscribe();
    }
    if (this.unreadRefreshInterval) {
      clearInterval(this.unreadRefreshInterval);
    }
  }

}
